// TrancheDocumentApi.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Card, Button, Collapse, Badge, Table } from "react-bootstrap";
import {
  getTrancheDocumentApis,
  addTrancheDocumentApi,
  updateTrancheDocumentApi,
  deleteTrancheDocumentApi,
  getAllApiEndpoints,
  getApiEndpointById,
  getAllTrancheDocument,
} from "../../service/api";
import { showNotification } from "../../service/showNotification";
import { FaChevronDown, FaChevronUp, FaEdit, FaTrash } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import styles from "./TrancheDocumentApi.module.css";
import ConfirmationModal from "../../helper/ConfirmationModal";
import { GlobalModal } from "../../helper/GlobalModal";
import TrancheApiForm from "../../components/TrancheApiForm";
import { tracker } from "../../service/mixpanel";

const methodColors = {
  GET: "info",
  POST: "success",
  PUT: "warning",
  PATCH: "primary",
  DELETE: "danger",
};

const TrancheDocumentApi = () => {
  const [apis, setApis] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedApiId, setSelectedApiId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingApi, setEditingApi] = useState(null);
  const [apiEndpoints, setApiEndpoints] = useState([]);
  const [trancheColumns, setTrancheColumns] = useState([]);

  // Create column map for conditional columns
  const columnMap = useMemo(() => {
    const map = new Map();
    trancheColumns.forEach((col) => map.set(col._id, col.label));
    return map;
  }, [trancheColumns]);

  // Fetch APIs and Columns
  const fetchApis = async () => {
    setLoading(true);
    try {
      const res = await getTrancheDocumentApis();
      let apiData = [];
      if (res?.data?.data && Array.isArray(res.data.data)) {
        apiData = res.data.data;
      } else if (Array.isArray(res?.data)) {
        apiData = res.data;
      } else if (Array.isArray(res)) {
        apiData = res;
      }
      setApis(apiData);
    } catch (err) {
      showNotification("error", "Failed to fetch Tranche APIs");
    } finally {
      setLoading(false);
    }
  };

  // Fetch API Endpoints
  const fetchApiEndpoints = async () => {
    try {
      const res = await getAllApiEndpoints();
      // Handle both direct array response and { data: [...] } structure
      const endpointsData = Array.isArray(res) ? res : res?.data || [];
      setApiEndpoints(endpointsData?.data);
    } catch (err) {
      console.error("Failed to fetch API endpoints:", err);
    }
  };

  // Fetch Tranche Columns for conditional columns
  const fetchTrancheColumns = async () => {
    try {
      const res = await getAllTrancheDocument();
      // Handle both direct array response and { data: [...] } structure
      const columnsData = Array.isArray(res) ? res : res?.data || [];
      setTrancheColumns(columnsData);
    } catch (err) {
      console.error("Failed to fetch tranche columns:", err);
    }
  };

  useEffect(() => {
    fetchApis();
    fetchApiEndpoints();
    fetchTrancheColumns();
  }, []);

  // Submit Form
  const handleFormSubmit = async (data) => {
    console.log("handleFormSubmit", data)
    try {
      if (editingApi) {
        tracker.tranches.trancheApiEditSubmit(editingApi._id);
        await updateTrancheDocumentApi(editingApi._id, data);
        showNotification("success", "API updated successfully");
      } else {
        tracker.tranches.trancheApiAddSubmit();
        await addTrancheDocumentApi(data);
        showNotification("success", "API created successfully");
      }

      setShowFormModal(false);
      setEditingApi(null);
      fetchApis();
    } catch (error) {
      showNotification(
        "error",
        `Failed to ${editingApi ? "update" : "create"} API`
      );
    }
  };

  // Delete API
  const handleDelete = async () => {
    try {
      tracker.tranches.trancheApiDeleteConfirm(selectedApiId);
      await deleteTrancheDocumentApi(selectedApiId);
      showNotification("success", "API deleted successfully");
      fetchApis();
    } catch (error) {
      showNotification("error", "Failed to delete API");
    } finally {
      setShowDeleteModal(false);
      setSelectedApiId(null);
    }
  };

  // Open Modals
  const openDeleteModal = (id) => {
    tracker.tranches.trancheApiDeleteClick(id);
    setSelectedApiId(id);
    setShowDeleteModal(true);
  };

  const openEditModal = (api) => {
    tracker.tranches.trancheApiEditClick(api.name);
    setEditingApi(api);
    setShowFormModal(true);
  };

  const openAddModal = () => {
    tracker.tranches.trancheApiAddClick();
    setEditingApi(null);
    setShowFormModal(true);
  };

  // Render details table for headers, params, body
  const renderDetailsTable = (title, data, isHeaders = false) => {
    const entries = Object.entries(data || {});
    if (entries.length === 0) return null;

    return (
      <div className={styles.section}>
        <h6>{title}</h6>
        <Table striped bordered size="md">
          <thead>
            <tr>
              <th style={{ width: "50%" }}>{isHeaders ? "Header Name" : "Column Name"}</th>
              <th style={{ width: "50%" }}>{isHeaders ? "Header Value" : "JSON Key"}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key}>
                <td>
                  {isHeaders ? key : columnMap.get(value) || value}
                </td>
                <td>{isHeaders ? value : key}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  // Render conditional variables as badges
  const renderConditionalVars = (data) => {
    if (!data || data.length === 0) return null;
    return (
      <div className={styles.section}>
        <h6>Conditional Variables</h6>
        <div className={styles.chipsContainer}>
          {data.map((colId) => (
            <Badge key={colId} bg="primary" className="me-1">
              {columnMap.get(colId) || "Unknown"}
            </Badge>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <h3>Configured APIs</h3>
          <Button variant="dark" onClick={openAddModal}>
            Add API
          </Button>
        </div>

        {loading ? (
          <Skeleton count={3} height={100} />
        ) : apis.length === 0 ? (
          <div className={styles.noApiMessage}>No API Added</div>
        ) : (
          apis.map((api) => (
            <Card key={api._id} className={styles.apiCard}>
              <Card.Header
                className={styles.apiHeader}
                onClick={() =>
                  setExpanded(expanded === api._id ? null : api._id)
                }
              >
                <div className={styles.apiTitle}>
                  <Badge
                    bg={methodColors[api.method] || "secondary"}
                    className={styles.methodBadge}
                  >
                    {api.method}
                  </Badge>
                  <div>
                    <span className={styles.apiName}>{api.name}</span>
                    <span className={styles.apiUrl}>{api.url}</span>
                  </div>
                </div>
                <div className={styles.actionIcons}>
                  <FaEdit
                    className={styles.editIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(api);
                    }}
                  />
                  <FaTrash
                    className={styles.deleteIcons}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteModal(api._id);
                    }}
                  />
                  {expanded === api._id ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </Card.Header>
              <Collapse
                in={expanded === api._id}
                onClick={() => tracker.tranches.trancheApiDetailsView(api.name)}
              >
                <Card.Body className={styles.apiDetails}>
                  {renderDetailsTable("Headers", api.headers, true)}
                  {renderDetailsTable("Parameters", api.params)}
                  {renderDetailsTable("Body Payload", api.body)}
                  {renderConditionalVars(api.conditionalColumnIds)}
                </Card.Body>
              </Collapse>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      <GlobalModal
        isOpen={showFormModal}
        title={editingApi ? "Edit API" : "Add API"}
        onClose={() => {
          setShowFormModal(false);
          setEditingApi(null);
        }}
        size="lg"
      >
        <TrancheApiForm
          apiData={editingApi}
          apiEndpoints={apiEndpoints}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowFormModal(false)}
          onFetchApiEndpoint={getApiEndpointById}
        />
      </GlobalModal>

      {/* Delete Confirmation */}
      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete API"
        message="Are you sure you want to delete this API?"
      />
    </>
  );
};

export default TrancheDocumentApi;